/**
 * The clinic is read-only, and it says so before anything fails (CLA-1167).
 *
 * Until this existed, a lapsed clinic had no in-product statement of its own
 * state. The plan sweep runs overnight, nothing on screen changes, and the
 * first the clinician knows is a note that will not generate — mid-consult,
 * which is the worst possible moment to ask someone to make a purchase
 * decision, and the moment they are least able to.
 *
 * Deliberately NOT `TrialStrip` reused. A trial countdown exists to create
 * urgency about a deadline that has not arrived. This is the opposite
 * situation: the deadline has passed, urgency is spent, and the emotional
 * job is reassurance. The same component would say the wrong thing in the
 * wrong tone.
 *
 * Five decisions, in case they look arbitrary later:
 *
 * - **Not dismissible, but quiet.** Dismissible means it is gone by the time
 *   it matters and they are back to discovering by failure. Loud means
 *   punishing someone all day for a decision they may not have made. Muted
 *   surface, no red — this is a state, not an error.
 * - **It states, it does not nag.** One line, one link, no countdown, no
 *   "act now". A persistent nag on every screen, at a clinic that has
 *   already decided, is the pattern to avoid.
 * - **The disclosure is the point.** The line stops the surprise; the
 *   expansion answers "can I run tomorrow's list?", which is the question
 *   actually in their head and which nothing else answers.
 * - **Role-aware.** Only someone who can manage the clinic gets the plan
 *   link — never a button that will refuse you.
 * - **Web only.** iOS shows this state in the read-only Plan card that
 *   CLA-849 shipped; a persistent bar there carrying a plan link edges
 *   toward App Store 3.1.3(f), and there is no reason to go near it.
 *
 * The state comes from the server's `writes_locked`, which folds both halves
 * of the lock. Checking for the `free` slug here would be a copy of a policy
 * that lives in two settings rows.
 */
import { useState } from 'react'
import { Link } from 'react-router'
import { PauseCircle, ChevronDown, ChevronUp } from 'lucide-react'
import { useSubscription } from '@/api/subscription-hooks'
import { useAuthStore } from '@/stores/auth'

const STILL_WORKS = [
  'Read your notes, patients and documents',
  'Export anything',
  'Finish and sign a consultation already underway',
]

const PAUSED = [
  'New notes and AI transcription',
  'New patients and appointments',
  'Letters, telehealth, online booking and the store',
]

export function LapsedStrip() {
  const { data: sub } = useSubscription()
  const canManage = useAuthStore((s) => s.canManageClinic)()
  const [open, setOpen] = useState(false)

  // Fails quiet: if the subscription read is unavailable the app is still
  // entirely usable, and a banner asserting a state we cannot confirm is
  // worse than no banner.
  if (!sub?.writes_locked) return null

  return (
    <div className="shrink-0 bg-surface-tertiary border-b border-border-subtle">
      <div className="px-4 py-2 flex items-center gap-3 flex-wrap">
        <PauseCircle className="w-4 h-4 text-ink-faint shrink-0" aria-hidden />
        <p className="flex-1 min-w-[14rem] text-[13px] text-ink-secondary">
          <span className="font-medium text-ink">Read-only.</span> Your records are all here —
          you just can&rsquo;t add new ones.
        </p>
        <button
          onClick={() => setOpen((v) => !v)}
          className="shrink-0 text-xs text-ink-secondary hover:text-ink flex items-center gap-1"
          aria-expanded={open}
        >
          What still works
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {canManage && (
          <Link
            to="/settings/plan"
            className="shrink-0 text-xs font-medium link"
          >
            Choose a plan
          </Link>
        )}
      </div>

      {open && (
        <div className="px-4 pb-3 pt-1 grid gap-4 sm:grid-cols-2 text-xs">
          <div>
            <p className="font-medium text-ink mb-1">Still works</p>
            <ul className="space-y-0.5 text-ink-secondary list-none">
              {STILL_WORKS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-medium text-ink mb-1">Paused</p>
            <ul className="space-y-0.5 text-ink-faint list-none">
              {PAUSED.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
